import {
  Categories,
  createPageCategory,
  registerEvents,
  updateLastEvent,
} from '@/app/components/search/Analytics/utils';
import type { SearchParams } from '@/server/searchParams.ts';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import type { DealCard } from '@/app/components/search/utils/buildDealCards/dealCardTypes.ts';
import type { Product } from '@/domainLogic/index.ts';
import { isFlexibleSearchByDate } from '@/app/components/search/utils/api/isLiveSearchByDate.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { PLATFORM_DATA_PRODUCT_VARIANT } from '@/app/components/search/Analytics/utils/platformData.ts';
import { ITEMS_PER_PAGE } from '@/app/components/search/utils/pagerUtils.ts';

const PLATFORM_DATA_VIEW_ITEM_LIST_EVENT_NAME = 'view_item_list';

export type PlatformDataResultsItem = {
  brand: string;
  category: string;
  id: string;
  list_name: string;
  list_position: number;
  name: string;
  price: number;
  quantity: number;
  variant: string;
};

type CreatePlatformDataResultsItemProps = {
  searchParams: SearchParams;
  localization: Localization;
  product: Product;
  listPosition: number;
};
const createPlatformDataResultsItem = ({
  searchParams,
  localization,
  product,
  listPosition,
}: CreatePlatformDataResultsItemProps): PlatformDataResultsItem => {
  const { brand } = localization;
  const category = createPageCategory(searchParams);
  const id = `${product?.accommodation?.hotelId || ''}`;
  const name = product?.accommodation?.name || '';
  const price = product?.rate?.price?.price ?? 0;
  const variants = [];
  if (isFlexibleSearchByDate(searchParams[URLParameters.DATE_TO])) {
    variants.push(PLATFORM_DATA_PRODUCT_VARIANT.FLEXIBLE);
  }
  if (product?.promotions?.flashSale) {
    variants.push(PLATFORM_DATA_PRODUCT_VARIANT.FLASH_SALES);
  }
  const variant = variants.join(',');

  return {
    brand,
    category,
    id,
    list_name: Categories.resultsList,
    list_position: listPosition,
    name,
    price,
    quantity: 1,
    variant,
  };
};

type CreatePlatformDataResultsItemsProps = {
  searchParams: SearchParams;
  localization: Localization;
  dealCards?: DealCard[];
};
const createPlatformDataResultsItems = ({
  searchParams,
  localization,
  dealCards,
}: CreatePlatformDataResultsItemsProps): PlatformDataResultsItem[] => {
  const page: number = parseInt(searchParams[URLParameters.PAGE] || '0', 10);
  const offset = page * ITEMS_PER_PAGE;
  return (
    dealCards?.map((dealCard) =>
      createPlatformDataResultsItem({
        searchParams,
        localization,
        product: dealCard.product,
        listPosition: offset + dealCard.index + 1,
      })
    ) || []
  );
};

type AnalyticsViewItemListEventProps = {
  searchParams: SearchParams;
  localization: Localization;
  dealCards?: DealCard[];
};
export const analyticsViewItemListEvent = ({
  searchParams,
  localization,
  dealCards,
}: AnalyticsViewItemListEventProps) => {
  const items = createPlatformDataResultsItems({
    searchParams,
    localization,
    dealCards,
  });

  const data = {
    event: PLATFORM_DATA_VIEW_ITEM_LIST_EVENT_NAME,
    items,
  };

  registerEvents(data);
};

type UpdateAnalyticsViewItemListEventProps = {
  searchParams: SearchParams;
  localization: Localization;
  dealCards?: DealCard[];
};
export const updateAnalyticsViewItemListEvent = ({
  searchParams,
  localization,
  dealCards,
}: UpdateAnalyticsViewItemListEventProps) => {
  const items = createPlatformDataResultsItems({
    searchParams,
    localization,
    dealCards,
  });

  const data = {
    event: PLATFORM_DATA_VIEW_ITEM_LIST_EVENT_NAME,
    items,
  };

  updateLastEvent(PLATFORM_DATA_VIEW_ITEM_LIST_EVENT_NAME, data);
};
